import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useConversationStoreShallow } from 'src/store/conversationStore';
import * as yup from 'yup';

import { HEADQUARTERS_NAME, INVESTIGATION_ENTITY } from '@speeki/dictionary';

import {
    Country_Code,
    IncidentTypesListDocument,
    StartCaseReportDocument,
} from '@graphql/generated/graphql';

import { useCaseOwnerExclusion } from '@utils/hooks/useCaseOwnerExclusion';

import { getPath } from '@services/RouteService/paths';

import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import {
    conversationDetailsVar,
    currentLanguageVar,
    subsidiarySiteVar,
} from '@boot/reactiveVariables';
import { yupResolver } from '@hookform/resolvers/yup';
import { optionalSelect, requiredSelectV2 } from '@shared/validations';

export const useConversationForm = () => {
    const [showPersonalInformation, setShowPersonalInformation] =
        useState(false);
    const [isPersonalInfoMandatory, setIsPersonalInfoMandatory] = useState<
        boolean | undefined
    >(undefined);

    const { addConversationMessage } = useConversationStoreShallow([
        'addConversationMessage',
    ]);

    const conversationDetails = useReactiveVar(conversationDetailsVar);
    const subsidiarySiteOption = useReactiveVar(subsidiarySiteVar);

    const navigate = useNavigate();

    const { data: incidentTypesData, loading: loadingIncident } = useQuery(
        IncidentTypesListDocument,
        {
            context: {
                notifyOnError: true,
            },
            variables: {
                targetLanguage: currentLanguageVar(),
            },
        },
    );
    const { data: exclusionOwnerData, loading: loadingExclusion } =
        useCaseOwnerExclusion();

    const [startCaseReport, { loading: startCaseLoading }] = useMutation(
        StartCaseReportDocument,
        {
            context: {
                notifyOnError: true,
            },
            onError: () => {},
        },
    );

    const loading = loadingIncident || loadingExclusion;

    const incidentTypesList =
        incidentTypesData?.tenant?.incidentTypes?.map((incidentType, index) => {
            return {
                key: index,
                label: incidentType.name,
                value: incidentType.uuid,
            };
        }) || [];

    const isExclusionOwnerEnabled =
        exclusionOwnerData?.tenant.caseOwnerExclusion?.excludableUsers?.length;
    const exclusionOwnerList =
        exclusionOwnerData?.tenant.caseOwnerExclusion?.excludableUsers?.map(
            (element, index) => {
                return {
                    key: index,
                    label: element.name,
                    value: element.uuid,
                };
            },
        ) || [];

    const onSubmit = (data: DetailsSchemaForm) => {
        const isReporterData =
            !!data.email || !!data.firstName || !!data.lastName;

        const exclusionOwnerListFiltered =
            data?.caseExclusionOwnerList
                ?.filter((element) => !!element.value)
                .map((element) => element.value || '') || [];

        const reporterData = isReporterData
            ? {
                  email: data.email,
                  firstName: data.firstName,
                  lastName: data.lastName,
              }
            : undefined;

        const { investigationEntity, subsidiarySite, tenantUuid } =
            subsidiarySiteOption || {};
        const isHeadquarters = subsidiarySite?.value === HEADQUARTERS_NAME;

        const sourceSubsidiarySiteUuid = isHeadquarters
            ? tenantUuid
            : subsidiarySite?.value;
        const targetSubsidiarySiteUuid =
            investigationEntity === INVESTIGATION_ENTITY.HEADQUARTERS ||
            isHeadquarters
                ? tenantUuid
                : subsidiarySite?.value;

        const subsidiaryData = {
            sourceSubsidiarySiteUuid,
            targetSubsidiarySiteUuid,
        };

        void startCaseReport({
            onCompleted: (result) => {
                addConversationMessage({
                    ...result.conversation,
                    type: 'ASSISTANT',
                });
                conversationDetailsVar(data);

                navigate(
                    getPath({
                        params: { caseUuid: result?.conversation.uuid },
                        path: '/new-conversation/:caseUuid/report',
                    }),
                );
            },
            variables: {
                input: {
                    caseExclusionOwnerList: exclusionOwnerListFiltered.length
                        ? exclusionOwnerListFiltered
                        : undefined,
                    country: data.country.value as Country_Code,
                    incidentTypeUuid: data.incidentTypeUuid.value,
                    reporterData,
                    ...(subsidiarySiteOption && subsidiaryData),
                },
                targetLanguage: currentLanguageVar(),
            },
        });
    };

    const methods = useForm<DetailsSchemaForm>({
        defaultValues: conversationDetails,
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(DetailsSchema),
    });

    return {
        exclusionOwnerList,
        incidentTypesList,
        isExclusionOwnerEnabled,
        isPersonalInfoMandatory,
        loading,
        methods,
        onSubmit,
        setIsPersonalInfoMandatory,
        setShowPersonalInformation,
        showPersonalInformation,
        startCaseLoading,
    };
};

const DetailsSchema = yup.object({
    businessUnitUuid: yup.string(),
    caseExclusionOwnerList: yup.array().of(optionalSelect).nullable(),
    country: requiredSelectV2,
    email: yup
        .string()
        .email('INVALID_EMAIL')
        .when('personalInfoMandatory', {
            is: true,
            /*
             * Extends the base schema rather than replacing it. The previous
             * `() => yup.string().required(...)` silently dropped the format
             * check in mandatory mode - and with confirmation now gating
             * submission there, a mis-typed address would render the
             * confirmation UI, never deliver a code, and leave no way out.
             */
            then: (schema) => schema.required('FIELD_CANNOT_BE_EMPTY'),
        }),
    firstName: yup.string().when('personalInfoMandatory', {
        is: true,
        then: () => yup.string().required('FIELD_CANNOT_BE_EMPTY'),
    }),
    incidentTypeUuid: requiredSelectV2,
    investigationEntity: yup.string(),
    lastName: yup.string().when('personalInfoMandatory', {
        is: true,
        then: () => yup.string().required('FIELD_CANNOT_BE_EMPTY'),
    }),
    personalInfoMandatory: yup.boolean(),
});

export type DetailsSchemaForm = yup.InferType<typeof DetailsSchema>;
